/**
 * Parts Catalog View
 * Browse and manage ERP parts catalog - synced from Azure API
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Autocomplete,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useBOM } from '../contexts/BOMContext';

// ERPPart is now dynamic - can have any fields from the API
interface ERPPart {
  id: number;
  internal_part_number: string;
  [key: string]: any;  // Allow dynamic fields
}

// Column definition from server
interface ColumnDef {
  key: string;
  label: string;
  width?: number;
  primary?: boolean;
  hidden?: boolean;
  tooltip_key?: string;
  dynamic?: boolean;
}

interface PartsStats {
  total_parts: number;
  manufacturers: Array<{ name: string; code: string; display: string; count: number }>;
  vendor_codes: Array<{ code: string; display: string; count: number }>;
  categories: Array<{ name: string; count: number }>;
  last_sync?: {
    type: string;
    completed_at: string;
    records_fetched: number;
    records_created: number;
    records_updated: number;
  };
}

interface SyncLog {
  id: number;
  type: string;
  status: string;
  started_at: string;
  completed_at?: string;
  records_fetched: number;
  records_created: number;
  records_updated: number;
  error_message?: string;
}

const PartsView: React.FC = () => {
  const { showToast, setLoading } = useBOM();
  
  // State
  const [parts, setParts] = useState<ERPPart[]>([]);
  const [columns, setColumns] = useState<ColumnDef[]>([]);  // Dynamic columns from server
  const [stats, setStats] = useState<PartsStats | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [manufacturerFilter, setManufacturerFilter] = useState('');
  const [vendorCodeFilter, setVendorCodeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalParts, setTotalParts] = useState(0);
  const [loading, setLoadingState] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Helper to get nested value from object (supports "a.b.c" keys)
  const getNestedValue = (obj: any, key: string): any => {
    if (!obj || !key) return null;
    const parts = key.split('.');
    let value = obj;
    for (const part of parts) {
      if (value == null) return null;
      value = value[part];
    }
    return value;
  };
  
  // Sync state
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [activeSyncId, setActiveSyncId] = useState<number | null>(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Dialogs
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  
  // Category edit state
  const [editingCategoryPartId, setEditingCategoryPartId] = useState<number | null>(null);
  const [editingCategoryValue, setEditingCategoryValue] = useState<string>('');
  const [newCategoryDialogOpen, setNewCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDisplayName, setNewCategoryDisplayName] = useState('');
  
  // New part form
  const [newPart, setNewPart] = useState({
    internal_part_number: '',
    external_reference: '',
    description: '',
    manufacturer_name: '',
    manufacturer_part_number: '',
    base_unit_of_measure: 'EA',
  });

  // Category options for filter (from Admin component types)
  const [categoryOptions, setCategoryOptions] = useState<Array<{name: string; display_name: string; count: number}>>([]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const response = await fetch('/api/erp/parts/stats');
      const data = await response.json();
      if (data.success) {
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  // Load categories from Admin component types
  const loadCategories = useCallback(async () => {
    try {
      const response = await fetch('/api/erp/parts/categories');
      const data = await response.json();
      if (data.success) {
        setCategoryOptions(data.categories);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, []);

  // Update a part's category
  const updatePartCategory = useCallback(async (partId: number, categoryName: string, createNew: boolean = false, displayName?: string) => {
    try {
      const response = await fetch(`/api/erp/parts/${partId}/category`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_name: categoryName,
          create_new: createNew,
          display_name: displayName
        })
      });
      const data = await response.json();
      if (data.success) {
        // Update local state
        setParts(prev => prev.map(p => 
          p.id === partId 
            ? { ...p, item_category: data.category, category_display: data.category_display }
            : p
        ));
        // Refresh categories to update counts
        loadCategories();
        setEditingCategoryPartId(null);
        setEditingCategoryValue('');
      } else {
        console.error('Failed to update category:', data.message);
      }
    } catch (error) {
      console.error('Failed to update category:', error);
    }
  }, [loadCategories]);

  // Handle adding new category
  const handleAddNewCategory = useCallback(async () => {
    if (!newCategoryName.trim() || editingCategoryPartId === null) return;
    
    await updatePartCategory(
      editingCategoryPartId,
      newCategoryName.trim(),
      true,
      newCategoryDisplayName.trim() || newCategoryName.trim()
    );
    
    setNewCategoryDialogOpen(false);
    setNewCategoryName('');
    setNewCategoryDisplayName('');
  }, [newCategoryName, newCategoryDisplayName, editingCategoryPartId, updatePartCategory]);

  // Load sync history
  const loadSyncHistory = useCallback(async () => {
    try {
      const response = await fetch('/api/erp/sync/history');
      const data = await response.json();
      if (data.success) {
        setSyncLogs(data.syncs);
        const staleMs = 30 * 60 * 1000;
        const running = data.syncs.find((s: SyncLog) => {
          if (s.status !== 'running') return false;
          const started = s.started_at ? new Date(s.started_at).getTime() : 0;
          return Number.isFinite(started) && Date.now() - started < staleMs;
        });
        if (running) {
          setSyncInProgress(true);
          setActiveSyncId(running.id);
        }
      }
    } catch (error) {
      console.error('Failed to load sync history:', error);
    }
  }, []);

  // Load parts
  const loadParts = useCallback(async () => {
    setLoadingState(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (manufacturerFilter) params.append('manufacturer', manufacturerFilter);
      if (vendorCodeFilter) params.append('vendor_code', vendorCodeFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      params.append('limit', String(rowsPerPage));
      params.append('offset', String(page * rowsPerPage));

      const response = await fetch(`/api/erp/parts?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setParts(data.parts);
        setTotalParts(data.total);
        // Set columns from server response (if provided)
        if (data.columns) {
          setColumns(data.columns);
        }
      }
    } catch (error: any) {
      showToast('error', `Failed to load parts: ${error.message}`);
    } finally {
      setLoadingState(false);
    }
  }, [searchQuery, manufacturerFilter, vendorCodeFilter, categoryFilter, page, rowsPerPage, showToast]);

  // Load on mount
  useEffect(() => {
    loadStats();
    loadSyncHistory();
    loadCategories();
  }, [loadStats, loadSyncHistory, loadCategories]);

  // Load parts when filters change
  useEffect(() => {
    loadParts();
  }, [loadParts]);

  // Poll sync status while sync is in progress
  useEffect(() => {
    if (syncInProgress && activeSyncId) {
      const interval = setInterval(async () => {
        try {
          const response = await fetch(`/api/erp/sync/status/${activeSyncId}`);
          const data = await response.json();
          if (data.success && data.sync.status !== 'running') {
            setSyncInProgress(false);
            setActiveSyncId(null);
            loadStats();
            loadParts();
            loadSyncHistory();
            if (data.sync.status === 'completed') {
              showToast('success', `Sync completed: ${data.sync.records_created} created, ${data.sync.records_updated} updated`);
            } else if (data.sync.status === 'failed') {
              showToast('error', `Sync failed: ${data.sync.error_message}`);
            }
          }
        } catch (error) {
          console.error('Failed to check sync status:', error);
        }
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [syncInProgress, activeSyncId, loadStats, loadParts, loadSyncHistory, showToast]);

  // Handle search with debounce
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(0);
    
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    const timer = setTimeout(() => {
      // loadParts will be triggered by useEffect
    }, 300);
    setSearchDebounceTimer(timer);
  };

  // Trigger full sync
  const handleFullSync = async () => {
    try {
      setSyncInProgress(true);
      const response = await fetch('/api/erp/sync/full', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_date: '2020-01-01',
          to_date: new Date().toISOString().split('T')[0],
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setSyncInProgress(false);
        showToast('error', data.detail || data.message || 'Failed to start sync');
        return;
      }
      setActiveSyncId(data.sync_id);
      showToast('info', 'Full sync started...');
      setSyncDialogOpen(false);
    } catch (error: any) {
      setSyncInProgress(false);
      showToast('error', `Sync failed: ${error.message}`);
    }
  };

  // Trigger incremental sync
  const handleIncrementalSync = async () => {
    try {
      setSyncInProgress(true);
      const response = await fetch('/api/erp/sync/incremental', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setSyncInProgress(false);
        showToast('error', data.detail || data.message || 'Failed to start sync');
        return;
      }
      setActiveSyncId(data.sync_id);
      showToast('info', `Incremental sync started from ${data.from_date}...`);
      setSyncDialogOpen(false);
    } catch (error: any) {
      setSyncInProgress(false);
      showToast('error', `Sync failed: ${error.message}`);
    }
  };

  const handleAddPart = async () => {
    if (!newPart.internal_part_number) {
      showToast('error', 'Internal part number is required');
      return;
    }

    try {
      const response = await fetch('/api/parts/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPart),
      });
      
      const data = await response.json();
      
      if (data.success) {
        showToast('success', 'Part added successfully');
        setAddDialogOpen(false);
        setNewPart({
          internal_part_number: '',
          external_reference: '',
          description: '',
          manufacturer_name: '',
          manufacturer_part_number: '',
          base_unit_of_measure: 'EA',
        });
        loadStats();
        loadParts();
      }
    } catch (error: any) {
      showToast('error', `Failed to add part: ${error.message}`);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    const normalised = dateStr.replace(/(\.\d{3})\d+/, '$1');
    const parsed = new Date(normalised);
    if (Number.isNaN(parsed.getTime())) return dateStr;
    return parsed.toLocaleString('en-GB');
  };

  const getSyncStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <SuccessIcon color="success" fontSize="small" />;
      case 'failed': return <ErrorIcon color="error" fontSize="small" />;
      case 'running': return <PendingIcon color="warning" fontSize="small" />;
      default: return null;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          ERP Parts Catalog
        </Typography>
        
        {/* Stats */}
        {stats && (
          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label={`Total: ${stats.total_parts.toLocaleString()}`} color="primary" />
            {stats.last_sync && (
              <Chip 
                label={`Last sync: ${formatDate(stats.last_sync.completed_at)}`} 
                color="default"
                variant="outlined"
              />
            )}
            {syncInProgress && (
              <Chip 
                icon={<CircularProgress size={16} />}
                label="Sync in progress..."
                color="warning"
              />
            )}
          </Box>
        )}
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Parts Catalog" />
          <Tab label="Sync History" icon={<HistoryIcon />} iconPosition="end" />
        </Tabs>
      </Paper>

      {/* Parts Catalog Tab */}
      {activeTab === 0 && (
        <>
          {/* Actions Bar */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search parts..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  size="small"
                  options={stats?.manufacturers || []}
                  getOptionLabel={(option) => typeof option === 'string' ? option : (option.display || option.code || '')}
                  value={stats?.manufacturers?.find(m => m.code === manufacturerFilter) || null}
                  onChange={(_, v) => { setManufacturerFilter(v?.code || ''); setPage(0); }}
                  isOptionEqualToValue={(option, value) => option.code === value?.code}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Manufacturer" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  size="small"
                  options={stats?.vendor_codes || []}
                  getOptionLabel={(option) => typeof option === 'string' ? option : `${option.code} (${option.count})`}
                  value={stats?.vendor_codes?.find(v => v.code === vendorCodeFilter) || null}
                  onChange={(_, v) => { setVendorCodeFilter(v?.code || ''); setPage(0); }}
                  isOptionEqualToValue={(option, value) => option.code === value?.code}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Vendor Code" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  size="small"
                  options={categoryOptions}
                  getOptionLabel={(option) => typeof option === 'string' ? option : `${option.display_name} (${option.count})`}
                  value={categoryOptions.find(c => c.name === categoryFilter) || null}
                  onChange={(_, v) => { setCategoryFilter(v?.name || ''); setPage(0); }}
                  isOptionEqualToValue={(option, value) => option.name === value.name}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Category" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={syncInProgress ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                  onClick={() => setSyncDialogOpen(true)}
                  disabled={syncInProgress}
                >
                  {syncInProgress ? 'Syncing...' : 'Sync from ERP'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Parts Table */}
          <Paper>
            {loading && <LinearProgress />}
            
            {!loading && parts.length === 0 && (
              <Alert severity="info" sx={{ m: 2 }}>
                {searchQuery ? 'No parts found matching your search' : 'No parts in catalog. Click "Sync from ERP" to import parts.'}
              </Alert>
            )}
            
            {parts.length > 0 && (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'primary.main' }}>
                        {/* Dynamic columns from server */}
                        {columns
                          .filter(col => !col.hidden)
                          .map(col => (
                            <TableCell 
                              key={col.key}
                              sx={{ 
                                color: 'white', 
                                fontWeight: 600,
                                width: col.width || 'auto',
                                minWidth: col.width || 'auto',
                                // Category column has different styling
                                ...(col.key === 'category_display' && {
                                  backgroundColor: '#1a1a1a',  // Black shade for category
                                }),
                              }}
                            >
                              {col.label}
                              {col.key === 'category_display' && (
                                <EditIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.7, verticalAlign: 'middle' }} />
                              )}
                            </TableCell>
                          ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parts.map((part) => (
                        <TableRow key={part.id} hover>
                          {/* Dynamic cells based on columns */}
                          {columns
                            .filter(col => !col.hidden)
                            .map(col => {
                              const value = getNestedValue(part, col.key);
                              const tooltipValue = col.tooltip_key ? getNestedValue(part, col.tooltip_key) : null;
                              const isCategoryColumn = col.key === 'category_display';
                              const isEditingThisCell = isCategoryColumn && editingCategoryPartId === part.id;
                              
                              // Special handling for Category column - editable
                              if (isCategoryColumn) {
                                return (
                                  <TableCell 
                                    key={col.key}
                                    sx={{
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',  // Light grey background
                                      cursor: 'pointer',
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                      },
                                      minWidth: 150,
                                    }}
                                    onClick={() => {
                                      if (!isEditingThisCell) {
                                        setEditingCategoryPartId(part.id);
                                        setEditingCategoryValue(part.item_category || '');
                                      }
                                    }}
                                  >
                                    {isEditingThisCell ? (
                                      <Autocomplete
                                        size="small"
                                        open
                                        autoFocus
                                        disableClearable={false}
                                        options={[
                                          ...categoryOptions.map(c => ({ 
                                            name: c.name, 
                                            display: `${c.display_name} (${c.count})`,
                                            displayName: c.display_name,
                                            count: c.count,
                                            isNew: false 
                                          })),
                                          { name: '__ADD_NEW__', display: '+ Add New Component Type...', displayName: '', count: 0, isNew: true }
                                        ]}
                                        getOptionLabel={(opt) => opt.display}
                                        filterOptions={(options, { inputValue }) => {
                                          const filtered = options.filter(opt => 
                                            opt.isNew || 
                                            opt.display.toLowerCase().includes(inputValue.toLowerCase()) ||
                                            opt.name.toLowerCase().includes(inputValue.toLowerCase())
                                          );
                                          // Always keep "Add New" at the end
                                          const addNew = filtered.find(o => o.isNew);
                                          const others = filtered.filter(o => !o.isNew);
                                          return addNew ? [...others, addNew] : others;
                                        }}
                                        value={(() => {
                                          const found = categoryOptions.find(c => c.name === editingCategoryValue);
                                          return found ? { name: found.name, display: `${found.display_name} (${found.count})`, displayName: found.display_name, count: found.count, isNew: false } : null;
                                        })()}
                                        onChange={(_, newValue) => {
                                          if (newValue && typeof newValue !== 'string') {
                                            if (newValue.isNew) {
                                              // Open new component type dialog
                                              setNewCategoryDialogOpen(true);
                                            } else {
                                              // Update with existing component type
                                              updatePartCategory(part.id, newValue.name);
                                            }
                                          } else if (newValue === null) {
                                            // Clear category
                                            updatePartCategory(part.id, '');
                                          }
                                        }}
                                        onClose={() => {
                                          setEditingCategoryPartId(null);
                                          setEditingCategoryValue('');
                                        }}
                                        renderOption={(props, option) => (
                                          <li {...props} style={{
                                            ...(option.isNew ? { 
                                              color: '#1a1a1a', 
                                              fontWeight: 600,
                                              borderTop: '1px solid #ddd',
                                              marginTop: 8,
                                              paddingTop: 8
                                            } : {})
                                          }}>
                                            {option.isNew ? option.display : (
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <span>{option.displayName}</span>
                                                <span style={{ color: '#888', fontSize: '0.85em' }}>({option.count})</span>
                                              </Box>
                                            )}
                                          </li>
                                        )}
                                        renderInput={(params) => (
                                          <TextField 
                                            {...params} 
                                            variant="standard"
                                            placeholder="Search component types..."
                                            autoFocus
                                            sx={{ minWidth: 180 }}
                                          />
                                        )}
                                        sx={{ minWidth: 180 }}
                                        ListboxProps={{
                                          style: { maxHeight: 300 }
                                        }}
                                      />
                                    ) : (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Chip
                                          label={value || 'Uncategorized'}
                                          size="small"
                                          variant={value ? 'filled' : 'outlined'}
                                          sx={{
                                            backgroundColor: value ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                                            color: value ? '#1a1a1a' : 'text.secondary',
                                            fontWeight: 500,
                                            '&:hover': {
                                              backgroundColor: 'rgba(0, 0, 0, 0.15)',
                                            }
                                          }}
                                        />
                                        <EditIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.5 }} />
                                      </Box>
                                    )}
                                  </TableCell>
                                );
                              }
                              
                              return (
                                <TableCell key={col.key}>
                                  {col.primary ? (
                                    <Typography variant="body2" fontWeight={600}>
                                      {value || '-'}
                                    </Typography>
                                  ) : tooltipValue ? (
                                    <Tooltip title={tooltipValue}>
                                      <span>{value || '-'}</span>
                                    </Tooltip>
                                  ) : (
                                    value || '-'
                                  )}
                                </TableCell>
                              );
                            })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  component="div"
                  count={totalParts}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100]}
                />
              </>
            )}
          </Paper>
        </>
      )}

      {/* Sync History Tab */}
      {activeTab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Sync History</Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadSyncHistory}
            >
              Refresh
            </Button>
          </Box>
          
          {syncLogs.length === 0 ? (
            <Alert severity="info">No sync history yet</Alert>
          ) : (
            <List>
              {syncLogs.map((log, index) => (
                <React.Fragment key={log.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getSyncStatusIcon(log.status)}
                          <Typography variant="body1" fontWeight={500}>
                            {log.type === 'full' ? 'Full Sync' : 'Incremental Sync'}
                          </Typography>
                          <Chip 
                            label={log.status} 
                            size="small" 
                            color={log.status === 'completed' ? 'success' : log.status === 'failed' ? 'error' : 'warning'}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Started: {formatDate(log.started_at)}
                            {log.completed_at && ` | Completed: ${formatDate(log.completed_at)}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Fetched: {log.records_fetched} | Created: {log.records_created} | Updated: {log.records_updated}
                          </Typography>
                          {log.error_message && (
                            <Typography variant="body2" color="error">
                              Error: {log.error_message}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < syncLogs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Sync Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sync Parts from ERP</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Sync parts data from the Azure ERP API. Choose the sync type:
          </Alert>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>Incremental Sync</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Fetches only parts modified since the last sync. Fast and efficient for daily updates.
              </Typography>
              <Button
                variant="contained"
                onClick={handleIncrementalSync}
                disabled={syncInProgress}
                fullWidth
              >
                Run Incremental Sync
              </Button>
            </Paper>
            
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>Full Sync</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Fetches all parts from the ERP system. Use for initial setup or complete refresh.
                May take several minutes for large catalogs.
              </Typography>
              <Button
                variant="outlined"
                onClick={handleFullSync}
                disabled={syncInProgress}
                fullWidth
              >
                Run Full Sync
              </Button>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add New Component Type Dialog */}
      <Dialog open={newCategoryDialogOpen} onClose={() => setNewCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', borderBottom: '2px solid #1a1a1a' }}>
          Add New Component Type
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Type Name"
              placeholder="e.g., POWER_SUPPLY"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
              helperText="Internal name (uppercase, underscores for spaces)"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Display Name"
              placeholder="e.g., Power Supply"
              value={newCategoryDisplayName}
              onChange={(e) => setNewCategoryDisplayName(e.target.value)}
              helperText="Human-readable name shown in the UI"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => {
            setNewCategoryDialogOpen(false);
            setNewCategoryName('');
            setNewCategoryDisplayName('');
            setEditingCategoryPartId(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddNewCategory}
            disabled={!newCategoryName.trim()}
            sx={{ backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#333' } }}
          >
            Add & Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Part Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Part</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Internal Part Number *"
                value={newPart.internal_part_number}
                onChange={(e) => setNewPart({ ...newPart, internal_part_number: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="External Reference"
                value={newPart.external_reference}
                onChange={(e) => setNewPart({ ...newPart, external_reference: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={newPart.description}
                onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Manufacturer Name"
                value={newPart.manufacturer_name}
                onChange={(e) => setNewPart({ ...newPart, manufacturer_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Manufacturer Part Number"
                value={newPart.manufacturer_part_number}
                onChange={(e) => setNewPart({ ...newPart, manufacturer_part_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Unit of Measure"
                value={newPart.base_unit_of_measure}
                onChange={(e) => setNewPart({ ...newPart, base_unit_of_measure: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddPart} variant="contained">Add Part</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartsView;
